import {
    type ComponentType,
    type FunctionComponent,
    type PropsWithChildren,
    useEffect,
    useState
} from "react";

// we need to use the original hook to apply the condition of needing the analytic client
// eslint-disable-next-line no-restricted-imports
import {
    type FeatureExposedProps,
    type FeatureExposedResponse,
    useFeatureExposed as useFeatureExposedOriginal,
    type WithFeatureExposedProps
} from "@atlassian/feature-exposed";

import { GASv3 } from "../../analytics/atl-analytics/gasv3";
import { useAnalyticsClient } from "../../analytics/wac-analytics/analytics-client";

/**
 * useFeatureExposed
 *
 * Hook that indicates a user is enrolled into a particular feature.
 * @param experimentKey name of the past experiment
 * @param group name of the group that this version of the code belongs to
 * @param conditions conditions that must be met for the feature exposed to be triggered
 * @param metadata additional metadata to include in the feature exposed event
 * @returns FeatureExposedResponse
 *
 * @link https://data-portal.internal.atlassian.com/analytics/registry/29804
 *
 * @example
 * const { hasFired } = useFeatureExposed("experiment-name", "group", { conditions: [isInView] });
 */
export function useFeatureExposed(
    experimentKey: string,
    group: "Control" | "Test" | string,
    { conditions, metadata = {} }: FeatureExposedProps = {}
): FeatureExposedResponse {
    const analyticsClient = useAnalyticsClient();
    const [enrichedMetadata, setEnrichedMetadata] = useState<Record<
        string,
        unknown
    > | null>(null);

    useEffect(() => {
        if (!enrichedMetadata) {
            setEnrichedMetadata(GASv3.enrichEventData(metadata));
        }
    }, [enrichedMetadata, metadata]);

    return useFeatureExposedOriginal(experimentKey, group, {
        conditions: conditions
            ? [...conditions, !!analyticsClient]
            : [!!analyticsClient],
        metadata: enrichedMetadata ?? metadata
    });
}

/**
 * FeatureExposed
 *
 * Component that can be used to manually wrap an element to indicate a user is enrolled into a particular experiment.
 *
 * @example
 * <FeatureExposed experimentKey="experiment-name" group="control">
 *     <Component />
 * </FeatureExposed>
 */
export const FeatureExposed = ({
    experimentKey,
    group,
    conditions,
    metadata,
    children
}: PropsWithChildren<
    {
        /**
         * key of the experiment / feature
         */
        experimentKey: string;
        /**
         * name of the group that this version of the code belongs to
         */
        group: string;
    } & FeatureExposedProps
>) => {
    useFeatureExposed(experimentKey, group, { conditions, metadata });
    return children;
};

/**
 * withFeatureExposed
 *
 * @param InnerComponent actual component you want to track feature exposure for
 * @param experimentKey key of the experiment / feature
 * @param group name of the group that this version of the code belongs to
 * @param options default options to send with feature exposed event. Can be overridden by the component's props
 *
 * @example
 * const InnerComponent = () => <div />;
 * export const MyComponent = withFeatureExposed("experiment-name", "group", InnerComponent);
 */
export function withFeatureExposed<TProps>(
    experimentKey: string,
    group: string,
    InnerComponent: ComponentType<WithFeatureExposedProps<TProps>>,
    options?: FeatureExposedProps
): FunctionComponent<WithFeatureExposedProps<TProps>> {
    return ({ featureExposed, ...props }: WithFeatureExposedProps<TProps>) => {
        useFeatureExposed(experimentKey, group, {
            ...options,
            ...featureExposed
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return <InnerComponent {...(props as any)} />;
    };
}
